// ============= Imports =============
// External Dependencies
import { useMemo, useEffect } from 'react';

// Internal Dependencies
import { useFormConfig } from './useFormConfig';
import { useFormHandlers } from './useFormHandlers';
import { convertToTagOptions } from '../../../../utils/transformers';
import {
  FormState,
  TagOption,
  SharePointApiResponse,
  PropertyValue,
  AppState,
  Extras,
} from '../types';
import { AuthContextProps } from '@/contexts/AuthContext';
import { FormStackProps } from '@tw/tw-component-library';

// ============= Types =============
interface UseFormConfigurationProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  formData: FormState;
  paUserDetails: TagOption[];
  pendingRemovals: string[];
  setPendingRemovals: React.Dispatch<React.SetStateAction<string[]>>;
  handleTagsChange: (tags: TagOption[]) => void;
  handleFloorChange: (value: string) => void;
  setFormData: React.Dispatch<React.SetStateAction<FormState>>;
  setPaUserDetails: React.Dispatch<React.SetStateAction<TagOption[]>>;
  handleSuccess: () => void;
  fetchSharePointData: (email: string) => Promise<{
    paUserDetails: SharePointApiResponse[];
    roomFloorValues: PropertyValue[];
  } | null>;
  handleTagPickerSearch: (searchTerm: string) => Promise<TagOption[]>;
  tagPickerSuggestions: TagOption[];
  fetchSharePointUser: (props: {
    getAccessToken: AuthContextProps['getAccessToken'];
    searchValue: string;
  }) => Promise<SharePointApiResponse>;
}

// ============= Constants =============
const MAX_TAGS = 3;
const MIN_SEARCH_LENGTH = 3;

// ============= Hook =============
/**
 * Custom hook for managing form configuration and data fetching
 * @param props Configuration and callback properties
 * @returns Object containing form configuration and handlers
 */
export const useFormConfiguration = ({
  getAccessToken,
  state,
  setState,
  formData,
  paUserDetails,
  pendingRemovals,
  setPendingRemovals,
  handleTagsChange,
  handleFloorChange,
  setFormData,
  setPaUserDetails,
  handleSuccess,
  fetchSharePointData,
  handleTagPickerSearch,
  tagPickerSuggestions,
  fetchSharePointUser,
}: UseFormConfigurationProps) => {
  // ============= Memoized Values =============
  /**
   * Filtered pre-tags from PA user details
   */
  const memoizedPreTags = useMemo(
    () => paUserDetails.filter((tag) => tag.value && tag.label),
    [paUserDetails]
  );

  /**
   * Form configuration extras
   */
  const extras = useMemo(
    () => ({
      tagPicker: {
        maxTags: MAX_TAGS,
        preTags: memoizedPreTags,
        onSearch: handleTagPickerSearch,
        minSearchLength: MIN_SEARCH_LENGTH,
        onTagsChange: handleTagsChange,
      },
      defaultValues: {
        text: {
          value: formData.floorRoom,
          onChange: handleFloorChange,
        },
      },
    }),
    [
      memoizedPreTags,
      handleTagPickerSearch,
      handleTagsChange,
      formData.floorRoom,
      handleFloorChange,
    ]
  );

  // ============= Form Configuration =============
  const { formSchema } = useFormConfig(extras as unknown as Extras);

  const formHandlers = useFormHandlers({
    getAccessToken,
    email: state.email,
    handleFloorChange,
    onSuccess: handleSuccess,
    //TODO Fix Type Error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-expect-error
    setState,
    pendingRemovals,
    setPendingRemovals,
    formData,
    fetchSharePointUser,
  });

  /**
   * Complete form configuration
   */
  const formConfig = useMemo<FormStackProps>(
    () => ({
      formSchema,
      initialData: {
        twlegalPA: formData.twlegalPA,
        floorRoom: formData.floorRoom,
      },
      validateOnChange: false,
      validateOnBlur: false,
      additionalProps: {
        //@ts-expect-error i will need to fix this
        tagPickerSuggestions,
        tagPickerValue: formData.twlegalPA.length === 0 ? [] : paUserDetails,
      },
    }),
    [formSchema, formData, paUserDetails, tagPickerSuggestions]
  );

  // ============= Effects =============
  /**
   * Fetch initial user data when email changes
   */
  useEffect(() => {
    if (!state.email) return;

    const fetchUserData = async () => {
      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        const data = await fetchSharePointData(state.email);

        if (data) {
          //TODO Fix Type Error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-expect-error
          const tagsWithKeys = convertToTagOptions(data.paUserDetails);
          const newFloor = data.roomFloorValues?.[0]?.value || '';

          setPaUserDetails(tagsWithKeys);
          setFormData((prev) => ({
            ...prev,
            twlegalPA: tagsWithKeys.map((tag) => tag.value),
            floorRoom: newFloor,
          }));
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: `Error fetching user data: ${error}`,
        }));
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchUserData();
  }, [
    state.email,
    fetchSharePointData,
    setPaUserDetails,
    setFormData,
    setState,
  ]);

  return {
    formConfig,
    formHandlers,
  };
};
